import { gql, useMutation, useReactiveVar } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { Route, Redirect, RouteProps, useLocation } from 'react-router-dom';
import { isLoggedInVar, jwtToken } from '../../apollo';
import { VerifyTokenMutationInput, VerifyTokenMutationOutput } from '../../pages/member/dtos/login-mutation.dto';
import sweetAlert from 'sweetalert2';

interface LoginCheckLoaderProps extends RouteProps {
    children?: React.ReactNode;
}

/**
 * @alias 토큰 검증 하기 - 토큰이 유효한지 확인 mutation
 */
const VERIFY_TOKEN_MUTATION = gql`
    mutation verifyTokenMutation($verifyTokenInput: VerifyTokenInput!) {
        verifyToken(input: $verifyTokenInput) {
            success
            message
        }
    }
`;
function LoginCheckWrapper({ children, ...rest }: LoginCheckLoaderProps) {
    /**
     * @alias 토큰 검증 하기 - 토큰이 유효한지 확인 mutation
     */
    const [verifyTokenMutation] = useMutation<VerifyTokenMutationOutput, VerifyTokenMutationInput>(
        VERIFY_TOKEN_MUTATION,
    );
    const isLoggedIn = useReactiveVar(isLoggedInVar);
    const token = useReactiveVar(jwtToken);
    const location = useLocation<{ from?: string }>();
    // 인증 상태
    const [isAuthenticated, setIsAuthenticated] = useState<null | boolean>(null);

    // 토큰 검증을 위한 useEffect 훅
    useEffect(() => {
        const checkToken = async () => {
            if (token && isLoggedIn) {
                try {
                    const result = await verifyTokenMutation({
                        variables: {
                            verifyTokenInput: { token },
                        },
                    });
                    if (result?.data?.verifyToken?.success) {
                        setIsAuthenticated(true);
                    } else {
                        await sweetAlert.fire({
                            title: '실패',
                            html: `인증 과정에서 오류가 발생했습니다. 다시 로그인해주세요.(1)`,
                            icon: 'error',
                        });
                        setIsAuthenticated(false);
                        //jwt token init
                        sessionStorage.removeItem('jwttoken');
                        //global var init
                        isLoggedInVar(false);
                    }
                } catch (error: any) {
                    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                        await sweetAlert.fire({
                            title: '실패',
                            html: `세션이 만료되었습니다. 다시 로그인해주세요.`,
                            icon: 'error',
                        });
                    } else {
                        await sweetAlert.fire({
                            title: '실패',
                            html: `인증 과정에서 오류가 발생했습니다. 다시 로그인해주세요.(2)`,
                            icon: 'error',
                        });
                    }
                    setIsAuthenticated(false);
                    //jwt token init
                    sessionStorage.removeItem('jwttoken');
                    //global var init
                    isLoggedInVar(false);
                }
            }
        };
        checkToken();
    }, [token]);

    // 초기 상태에서는 아무것도 렌더링하지 않음
    if (isAuthenticated === null) {
        return null;
    }

    return (
        <Route
            {...rest}
            render={() => {
                return isAuthenticated ? (
                    children
                ) : (
                    <>
                        <Redirect to={{ pathname: '/member/login', state: { from: location.pathname } }} />
                    </>
                );
            }}
        />
    );
}
export default LoginCheckWrapper;
